import { CountryCodeEnum } from "@/lib/enum/country-code.enum";

/**
 * The VAT identifier prefix of Greece, which deviates from its ISO code GR.
 */
const GREECE_VAT_ID_PREFIX = "EL";

/**
 * The VAT identifier prefixes accepted by EN 16931 (BR-CO-09): every ISO 3166-1 alpha-2 country
 * code plus EL for Greece.
 */
const VAT_ID_COUNTRY_PREFIXES: ReadonlySet<string> = new Set<string>([
  ...Object.values(CountryCodeEnum),
  GREECE_VAT_ID_PREFIX
]);

/**
 * A VAT ID in the format of each country (EU VIES formats, plus the Swiss UID-MWST, the UK and
 * Norway), keyed by ISO 3166-1 alpha-2 code. Shown to users as an example of what to enter.
 */
const VAT_ID_EXAMPLES: Readonly<Partial<Record<CountryCodeEnum, string>>> = {
  [CountryCodeEnum.germany]: "DE123456789",
  [CountryCodeEnum.austria]: "ATU12345678",
  [CountryCodeEnum.switzerland]: "CHE-123.456.789 MWST",
  [CountryCodeEnum.france]: "FR12345678901",
  [CountryCodeEnum.poland]: "PL1234567890",
  [CountryCodeEnum.netherlands]: "NL123456789B01",
  [CountryCodeEnum.belgium]: "BE0123456789",
  [CountryCodeEnum.luxembourg]: "LU12345678",
  [CountryCodeEnum.italy]: "IT12345678901",
  [CountryCodeEnum.spain]: "ESX1234567X",
  [CountryCodeEnum.portugal]: "PT123456789",
  [CountryCodeEnum.denmark]: "DK12345678",
  [CountryCodeEnum.sweden]: "SE123456789001",
  [CountryCodeEnum.finland]: "FI12345678",
  [CountryCodeEnum.ireland]: "IE1234567X",
  [CountryCodeEnum.czechRepublic]: "CZ12345678",
  [CountryCodeEnum.slovakia]: "SK1234567890",
  [CountryCodeEnum.slovenia]: "SI12345678",
  [CountryCodeEnum.hungary]: "HU12345678",
  [CountryCodeEnum.croatia]: "HR12345678901",
  [CountryCodeEnum.romania]: "RO1234567890",
  [CountryCodeEnum.bulgaria]: "BG123456789",
  [CountryCodeEnum.greece]: "EL123456789",
  [CountryCodeEnum.cyprus]: "CY12345678X",
  [CountryCodeEnum.malta]: "MT12345678",
  [CountryCodeEnum.estonia]: "EE123456789",
  [CountryCodeEnum.latvia]: "LV12345678901",
  [CountryCodeEnum.lithuania]: "LT123456789",
  [CountryCodeEnum.unitedKingdom]: "GB123456789",
  [CountryCodeEnum.norway]: "NO123456789MVA"
};

/**
 * An example VAT ID in the format of the given country, falling back to the German format for a
 * country without one.
 */
export function vatIdExample(countryCode?: string | null): string {
  const example = countryCode ? VAT_ID_EXAMPLES[countryCode.trim().toUpperCase() as CountryCodeEnum] : undefined;

  return example ?? (VAT_ID_EXAMPLES[CountryCodeEnum.germany] as string);
}

/**
 * Whether a tax number is a VAT ID (USt-IdNr.) of any country, i.e. starts with a country prefix,
 * as opposed to a national tax number (Steuernummer) that starts with digits.
 *
 * Mirrors `isVatId` of the backend's e-invoice export, which decides by the same rule whether a
 * party's tax number becomes its VAT identifier (BT-31 / BT-48) or its tax registration (BT-32).
 */
export function isVatId(taxnumber?: string | null): boolean {
  if (!taxnumber) {
    return false;
  }

  return VAT_ID_COUNTRY_PREFIXES.has(taxnumber.trim().toUpperCase().slice(0, 2));
}
